import { h } from "@stencil/core";
import { getInlineCssColor } from "../../utils";
import { getAceTagNames } from "./ace-tag.get-names";
export class AceTag {
    constructor() {
        /** Type of the tag to render. This will define the background color, text color and potentially the icon. */
        this.type = 'default';
        /** Size of the tag. */
        this.size = 'default';
        /** Whether the background color of the tag is subtle. */
        this.subtle = false;
        // Tag configuration map
        this.tagConfigs = {
            'alert': {
                backgroundColor: 'surface-semantic--error',
                borderColor: 'surface-semantic--error',
                contentColor: 'text-neutral--primary-uni-light',
            },
            'primary': {
                backgroundColor: 'surface-semantic--new-and-live',
                borderColor: 'surface-semantic--new-and-live',
                contentColor: 'text-neutral--primary-uni-light',
            },
            'success': {
                backgroundColor: 'surface-semantic--success',
                borderColor: 'surface-semantic--success',
                contentColor: 'text-neutral--primary-uni-light',
            },
            'highlight': {
                backgroundColor: 'surface-semantic--highlight',
                borderColor: 'surface-semantic--highlight',
                contentColor: 'semantic-tag-inverted-text',
            },
            'vertical-sports': {
                backgroundColor: 'brand-verticals--sports',
                borderColor: 'brand-verticals--sports',
                contentColor: 'text-neutral--primary-uni-light',
                defaultIcon: 'vertical-sports',
            },
            'vertical-lottery': {
                backgroundColor: 'brand-verticals--lottery',
                borderColor: 'brand-verticals--lottery',
                contentColor: 'text-neutral--primary-uni-light',
                defaultIcon: 'vertical-lottery',
            },
            'vertical-casino': {
                backgroundColor: 'brand-verticals--casino',
                borderColor: 'brand-verticals--casino',
                contentColor: 'text-neutral--primary-uni-light',
                defaultIcon: 'vertical-casino',
            },
            'vertical-slots': {
                backgroundColor: 'brand-verticals--slots',
                borderColor: 'brand-verticals--slots',
                contentColor: 'text-neutral--primary-uni-light',
                defaultIcon: 'vertical-slots',
            },
            'vertical-promotions': {
                backgroundColor: 'brand-verticals--promotions',
                borderColor: 'brand-verticals--promotions',
                contentColor: 'semantic-tag-inverted-text',
                defaultIcon: 'vertical-promotions',
            },
            'game-tag': {
                backgroundColor: 'surface-solid--200',
                contentColor: 'text-neutral--primary',
            },
            'default': {
                backgroundColor: 'surface-solid--100',
                borderColor: 'borders--100',
                contentColor: 'text-neutral--primary',
            },
        };
    }
    componentWillLoad() {
        var _a;
        this.names = getAceTagNames((_a = this.hostElement) === null || _a === void 0 ? void 0 : _a.id);
        this.applyTagConfiguration();
    }
    applyTagConfiguration() {
        var _a;
        // Get tag config or default if not found
        const config = this.tagConfigs[this.type] || this.tagConfigs.default;
        // Set properties based on config
        this.backgroundColor = config.backgroundColor;
        this.borderColor = (_a = config.borderColor) !== null && _a !== void 0 ? _a : null;
        this.contentColor = this.subtle ? 'text-neutral--primary' : config.contentColor;
        // Only set default icon if no icon was provided and there is a default icon
        if (!this.iconName && config.defaultIcon) {
            this.iconName = config.defaultIcon;
        }
        // Special case for game-tag to null out the icon name
        if (this.type === 'game-tag') {
            this.iconName = null;
        }
    }
    getTextProps() {
        return this.size === 'small' ? { size: 'small', weight: 'normal' } : { size: 'regular', weight: 'semibold' };
    }
    renderLabel() {
        if (!this.label) {
            return null;
        }
        const { size, weight } = this.getTextProps();
        return (h("div", { class: this.names.CLASS.SLOT }, h("ace-text", { id: this.names.ID.LABEL, type: 'label', weight: weight, tag: 'label', size: size, color: this.contentColor, truncate: true }, this.label)));
    }
    renderGameTag() {
        const hasLabel = !!this.label;
        const imgSize = hasLabel ? { width: '44', height: '32' } : { width: '54', height: '40' };
        const cssClasses = {
            [this.names.CLASS.ROOT]: true,
            [this.names.CLASS.____GAME]: hasLabel,
            [this.names.CLASS.____GAME_ONLY_IMG]: !hasLabel,
        };
        return (h("span", { id: this.names.ID.ROOT, class: cssClasses, style: {
                '--ace-tag-background-color': getInlineCssColor(this.backgroundColor),
            } }, this.imageSrc && (h("div", { class: this.names.CLASS.IMAGE }, h("ace-image", { src: this.imageSrc, alt: this.label || '', width: imgSize.width, height: imgSize.height, "aspect-ratio": 'game-tile' }))), this.renderLabel()));
    }
    renderNormalTag() {
        const cssClasses = {
            [this.names.CLASS.ROOT]: true,
            [this.names.CLASS.____SMALL]: this.size === 'small',
            [this.names.CLASS.____SUBTLE]: this.subtle && this.type !== 'default',
        };
        return (h("span", { id: this.names.ID.ROOT, class: cssClasses, style: {
                '--ace-tag-background-color': getInlineCssColor(this.backgroundColor),
                '--ace-tag-border-color': getInlineCssColor(this.borderColor),
            } }, this.iconName && h("ace-icon", { id: this.names.ID.ICON, color: this.contentColor, name: this.iconName, size: 'xs' }), this.renderLabel()));
    }
    render() {
        return this.type === 'game-tag' ? this.renderGameTag() : this.renderNormalTag();
    }
    static get is() { return "ace-tag"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["./ace-tag.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["ace-tag.css"]
        };
    }
    static get properties() {
        return {
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IAceTagType",
                    "resolved": "\"alert\" | \"default\" | \"game-tag\" | \"highlight\" | \"primary\" | \"success\" | \"vertical-casino\" | \"vertical-lottery\" | \"vertical-promotions\" | \"vertical-slots\" | \"vertical-sports\"",
                    "references": {
                        "IAceTagType": {
                            "location": "import",
                            "path": "./ace-tag.types",
                            "id": "src/components/ace-tag/ace-tag.types.ts::IAceTagType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Type of the tag to render. This will define the background color, text color and potentially the icon."
                },
                "getter": false,
                "setter": false,
                "attribute": "type",
                "reflect": false,
                "defaultValue": "'default'"
            },
            "iconName": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "IAceIconName",
                    "resolved": "string",
                    "references": {
                        "IAceIconName": {
                            "location": "import",
                            "path": "../ace-icon/ace-icon.types",
                            "id": "src/components/ace-icon/ace-icon.types.ts::IAceIconName"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Name of the icon to render. In some cases it is automatically rendered when not passed."
                },
                "getter": false,
                "setter": false,
                "attribute": "icon-name",
                "reflect": false
            },
            "imageSrc": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "URL or path to the image to render in case we choose the \"game-tag\" type."
                },
                "getter": false,
                "setter": false,
                "attribute": "image-src",
                "reflect": false
            },
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The label to be shown in the badge."
                },
                "getter": false,
                "setter": false,
                "attribute": "label",
                "reflect": false
            },
            "size": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IAceTagSize",
                    "resolved": "\"default\" | \"small\"",
                    "references": {
                        "IAceTagSize": {
                            "location": "import",
                            "path": "./ace-tag.types",
                            "id": "src/components/ace-tag/ace-tag.types.ts::IAceTagSize"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Size of the tag."
                },
                "getter": false,
                "setter": false,
                "attribute": "size",
                "reflect": false,
                "defaultValue": "'default'"
            },
            "subtle": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Whether the background color of the tag is subtle."
                },
                "getter": false,
                "setter": false,
                "attribute": "subtle",
                "reflect": true,
                "defaultValue": "false"
            }
        };
    }
    static get elementRef() { return "hostElement"; }
}
//# sourceMappingURL=ace-tag.js.map
